import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {SETTINGS_NAVIGATION_DEFINITIONS} from "../system/settings/settingsNavigation";

type StaticDocumentTitleRoute = {
    path: string;
    titleParts: readonly string[];
}

export const STATIC_DOCUMENT_TITLE_ROUTES: readonly StaticDocumentTitleRoute[] = [
    {path: '/', titleParts: ['Home']},
    ...SETTINGS_NAVIGATION_DEFINITIONS.map(({path, label}) => ({
        path,
        titleParts: ['System', label],
    })),
    {path: '/system/availability', titleParts: ['System', 'Availability']},
    {path: '/system/paymentMethods', titleParts: ['System', 'Payment methods']},
    {path: '/system/mappingCodes', titleParts: ['System', 'Mapping codes']},
    {path: '/system/integrations', titleParts: ['System', 'Integrations']},
    {path: '/system', titleParts: ['System']},
]

type DocumentTitleOverride = {
    id: symbol;
    pathname: string;
    title: string;
}

type RegisterDocumentTitleOverride = (override: DocumentTitleOverride) => () => void

const DocumentTitleOverrideContext = createContext<RegisterDocumentTitleOverride | null>(null)

const normalizePathname = (pathname: string) =>
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

const routeMatches = (pathname: string, routePath: string) =>
    pathname === routePath || pathname.startsWith(`${routePath}/`)

const resolveStaticTitleParts = (pathname: string) => {
    const normalizedPathname = normalizePathname(pathname)
    return STATIC_DOCUMENT_TITLE_ROUTES
        .filter(route => routeMatches(normalizedPathname, route.path))
        .sort((left, right) => right.path.length - left.path.length)[0]
        ?.titleParts
}

export const useDocumentTitleOverride = (title: string | undefined) => {
    const registerOverride = useContext(DocumentTitleOverrideContext)
    const {pathname} = useLocation()
    const overrideId = useRef(Symbol('document-title-override'))

    useLayoutEffect(() => {
        if (!registerOverride || !title?.trim()) return undefined
        return registerOverride({id: overrideId.current, pathname, title})
    }, [pathname, registerOverride, title])

    if (!registerOverride) {
        throw new Error('useDocumentTitleOverride must be used within DocumentTitleManager')
    }
}

export const DocumentTitleManager = ({children}: PropsWithChildren) => {
    const {pathname} = useLocation()
    const {t} = useTranslation()
    const [overrides, setOverrides] = useState<DocumentTitleOverride[]>([])

    const registerOverride = useCallback<RegisterDocumentTitleOverride>((override) => {
        setOverrides(current => [
            ...current.filter(candidate => candidate.id !== override.id),
            override,
        ])
        return () => {
            setOverrides(current => current.filter(candidate => candidate.id !== override.id))
        }
    }, [])

    const activeOverride = [...overrides]
        .reverse()
        .find(override => override.pathname === pathname)
    const staticTitle = resolveStaticTitleParts(pathname)
        ?.map(titlePart => t(titlePart))
        .join(' - ')
    const title = activeOverride?.title ?? staticTitle

    useLayoutEffect(() => {
        document.title = title ? `${title} - Crqlar` : 'Crqlar'
    }, [title])

    return (
        <DocumentTitleOverrideContext.Provider value={registerOverride}>
            {children}
        </DocumentTitleOverrideContext.Provider>
    )
}
